import { Controller } from "@hotwired/stimulus";
import {
  computePosition,
  flip,
  shift,
  offset,
  arrow,
  autoUpdate,
} from "@floating-ui/dom";
import { useHover, useClickOutside } from "stimulus-use";
import { enter, leave } from "el-transition";
import "./index.css";

export default class extends Controller {
  static targets = ["button", "card", "arrow", "searchbox"];

  static values = {
    opened: Boolean,
  };

  connect() {
    // Reclaim orphaned card from <body> after Turbo cache restore or bfcache.
    // When portaled, the card lives in <body> outside this element. If the page
    // is cached and restored, hasCardTarget is false because the card is orphaned.
    // We stamp each card with a UID before portaling so we can find it again.
    if (!this.hasCardTarget && this.element.dataset.dropdownUid) {
      const orphaned = document.querySelector(
        `[data-dropdown-owner="${this.element.dataset.dropdownUid}"]`
      );
      if (orphaned) {
        orphaned.classList.add("hidden");
        this.element.appendChild(orphaned);
      }
    }

    // Guard: ensure required targets exist
    if (!this.hasCardTarget || !this.hasArrowTarget || !this.hasButtonTarget) {
      console.warn("Dropdown controller missing required targets");
      return;
    }

    // Store reference BEFORE moving to body
    this._card = this.cardTarget;
    this._arrow = this.arrowTarget;

    // Stamp both elements with a UID so the card can be reclaimed after cache restore
    if (!this.element.dataset.dropdownUid) {
      this.element.dataset.dropdownUid = `dd-${Math.random().toString(36).slice(2, 9)}`;
    }
    this._card.dataset.dropdownOwner = this.element.dataset.dropdownUid;

    // Store reference to the input-select element for lazy controller lookup
    this._inputSelectElement = this.element.closest('[data-controller*="ui--input-select"]');

    // Move dropdown to body for portal behavior
    document.body.appendChild(this._card);

    // Handle clicks on menu options inside portaled dropdown
    this.handleMenuClick = (e) => {
      const menuOption = e.target.closest('[data-action*="ui--input-select#select"]');
      if (menuOption && this._inputSelectElement) {
        // Lazy lookup: resolve the controller at click time, not at connect time,
        // to avoid race conditions with Stimulus controller connection ordering
        const inputSelectController = this.application.getControllerForElementAndIdentifier(
          this._inputSelectElement,
          "ui--input-select"
        );

        if (inputSelectController) {
          e.stopPropagation();

          // Directly update the input and display targets
          const displayValue = menuOption.dataset.display || menuOption.innerText;
          const value = menuOption.dataset.value;

          inputSelectController.displayTarget.innerHTML = displayValue;
          inputSelectController.inputTarget.value = value;
          inputSelectController.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));

          this.hide();
        }
      }
    };
    this._card.addEventListener('click', this.handleMenuClick);

    // Manual click outside detection since dropdown is portaled
    // Using capture phase to ensure it runs before other handlers
    this.handleClickOutside = (e) => {
      // Only process if dropdown is open
      if (!this.openedValue) return;

      // Ignore if clicking on button or dropdown
      if (this.buttonTarget.contains(e.target) || this._card.contains(e.target)) {
        return;
      }

      this.hide();
    };

    // Use capture phase for better control with portaled elements
    document.addEventListener('click', this.handleClickOutside, true);

    // Listen for drawer opened event or Turbo navigation to close dropdown
    this.handleCloseDropdown = () => {
      if (this.openedValue) {
        this.hide();
      }
    };
    window.addEventListener('drawer:opened', this.handleCloseDropdown);
    document.addEventListener('turbo:before-visit', this.handleCloseDropdown);

    // Move card back into controller element before Turbo caches the page snapshot.
    // This is the first line of defense — the UID-based recovery in connect() is the fallback.
    this.handleBeforeCache = () => {
      if (this._card && this._card.parentNode === document.body) {
        this._card.classList.add("hidden");
        this.element.appendChild(this._card);
      }
    };
    document.addEventListener('turbo:before-cache', this.handleBeforeCache);

    autoUpdate(this.buttonTarget, this._card, this.update.bind(this));
    useHover(this, { element: this.element });

    this.timeout = null;
    this.setDropdownSize();
  }

  disconnect() {
    // Guard: if targets were missing, nothing to clean up
    if (!this._card) return;

    // Clean up: remove event listeners
    if (this.handleMenuClick) {
      this._card.removeEventListener('click', this.handleMenuClick);
    }
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside, true);
    }
    if (this.handleCloseDropdown) {
      window.removeEventListener('drawer:opened', this.handleCloseDropdown);
      document.removeEventListener('turbo:before-visit', this.handleCloseDropdown);
    }
    if (this.handleBeforeCache) {
      document.removeEventListener('turbo:before-cache', this.handleBeforeCache);
    }

    // Move card back into controller element so it's available for reconnection.
    // If turbo:before-cache already moved it back, this is a no-op.
    if (this._card && this._card.parentNode === document.body) {
      this._card.classList.add("hidden");
      this.element.appendChild(this._card);
    }
  }

  setDropdownSize() {
    const inputWrapper = this.element.closest(".inputWrapper");
    if (inputWrapper) {
      const width = inputWrapper.getBoundingClientRect().width;
      this._card.querySelector("div").style.width = String(width) + "px";
    }
  }

  mouseEnter() {
    clearTimeout(this.timeout);
  }

  mouseLeave() {
    const { hide, timeout } = this;
    clearTimeout(timeout);
    this.timeout = setTimeout(hide, 500);
  }

  clickOutside(e) {
    // if (this.element.getAttribute('contenteditable') === 'true') {
    //   this.revert();
    // }
    this.hide();
  }

  update() {
    const button = this.buttonTarget;
    const dropdown = this._card;
    const dropdownArrow = this._arrow;

    const defaultPlacement = dropdown.dataset
      ? dropdown.dataset.placement
      : "bottom";
    const offsetValue = dropdown.dataset.offset || 0;
    const paddingValue = dropdown.dataset.padding || 40;

    computePosition(button, dropdown, {
      strategy: "fixed",
      placement: defaultPlacement,
      middleware: [
        offset(Number(offsetValue)),
        flip(),
        // shift({ padding: Number(paddingValue) }),
        arrow({ element: dropdownArrow }),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(dropdown.style, {
        left: `${x}px`,
        top: `${y + 10}px`,
      });

      const { x: arrowX, y: arrowY } = middlewareData.arrow;

      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
      }[placement.split("-")[0]];

      dropdownArrow.style.left = arrowX != null ? `${arrowX}px` : "";
      dropdownArrow.style.top = arrowY != null ? `${arrowY}px` : "";
      dropdownArrow.style.right = "";
      dropdownArrow.style.bottom = "";
      dropdownArrow.style[staticSide] = "-4px";

      dropdownArrow.dataset.position = staticSide;

      if (staticSide === "top") {
        dropdownArrow.classList.add("border-b-0", "border-r-0");
      } else if (staticSide === "bottom") {
        dropdownArrow.classList.add("border-t-0", "border-l-0");
      } else if (staticSide === "left") {
        dropdownArrow.classList.add("border-t-0", "border-r-0");
      } else if (staticSide === "right") {
        dropdownArrow.classList.add("border-b-0", "border-l-0");
      }
    });
  }

  show(e) {
    e.preventDefault();

    // Don't stop propagation in capture phase - let click outside handler work
    // e.stopPropagation();

    if (this.openedValue) {
      this.hide();
      this.element.classList.remove("opened");
    } else {
      // Position FIRST, then show
      this.update();
      this._card.classList.remove("hidden");

      this.openedValue = true;
      this.element.classList.add("opened");
    }

    const buttonChild = this.buttonTarget.firstElementChild;
    if (buttonChild) {
      buttonChild.classList.add("opened");
    }

    const searchbleArea = this.element.querySelector(
      "[data-controller='ui--searchable']",
    );

    if (searchbleArea) {
      const input = searchbleArea.querySelector(
        "[data-ui--searchable-target='input']",
      );
      input.focus();
    }

    this.setDropdownSize();

    // Dispatch event for components that need to recalculate after dropdown opens
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("dropdown:opened", {
          detail: { dropdown: this._card },
        }),
      );
    }, 50);
  }

  hide() {
    if (this._card) {
      this._card.classList.add("hidden");
      this.openedValue = false;
    }

    if (this.buttonTarget) {
      const buttonChild = this.buttonTarget.firstElementChild;
      if (buttonChild) {
        buttonChild.classList.remove("opened");
      }
    }
  }
}
